import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSEO } from '../lib/useSEO';
import { ArrowLeft, Calendar, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();

  const SITE_URL = 'https://jepprep.com';
  const LOGO_IMG = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

  useSEO(
    post?.meta_title || (post ? `${post.title} | JepPrep Blog` : 'Blog | JepPrep'),
    post?.meta_description || post?.excerpt || '',
    post ? { canonical: `${SITE_URL}/blog/${post.slug}`, ogImage: LOGO_IMG } : {}
  );

  // Inject FAQ + Article + Breadcrumb JSON-LD structured data
  useEffect(() => {
    if (!post) return;
    const scripts = [];

    // FAQ schema
    if (post.faq_schema?.length) {
      const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faq_schema.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      };
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-blog-schema', 'faq');
      s.textContent = JSON.stringify(faqJsonLd);
      document.head.appendChild(s);
      scripts.push(s);
    }

    // Article schema (BlogPosting)
    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.meta_description || post.excerpt,
      "author": { "@type": "Organization", "name": post.author || "JepPrep Team", "url": SITE_URL },
      "publisher": {
        "@type": "Organization",
        "name": "JepPrep",
        "url": SITE_URL,
        "logo": { "@type": "ImageObject", "url": LOGO_IMG }
      },
      "url": `${SITE_URL}/blog/${post.slug}`,
      "mainEntityOfPage": `${SITE_URL}/blog/${post.slug}`,
      "datePublished": post.published_at,
      "dateModified": post.updated_at || post.published_at,
      "image": LOGO_IMG,
      "keywords": post.tags?.join(', ') || ''
    };
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.setAttribute('data-blog-schema', 'article');
    articleScript.textContent = JSON.stringify(articleJsonLd);
    document.head.appendChild(articleScript);
    scripts.push(articleScript);

    // Breadcrumb schema
    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": `${SITE_URL}/blog/${post.slug}` }
      ]
    };
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.setAttribute('data-blog-schema', 'breadcrumb');
    breadcrumbScript.textContent = JSON.stringify(breadcrumbJsonLd);
    document.head.appendChild(breadcrumbScript);
    scripts.push(breadcrumbScript);

    return () => {
      scripts.forEach(s => { if (s.parentNode) s.parentNode.removeChild(s); });
    };
  }, [post]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await api.get(`/api/blog/posts/${slug}`);
        setPost(response.data.post);
      } catch (error) {
        console.error('Failed to fetch blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const parseInline = (text, keyPrefix = '') => {
    const parts = [];
    // Match markdown links (both external https:// and internal /path) and bold **text**
    const regex = /\[([^\]]+)\]\(((?:https?:\/\/[^)]+)|(?:\/[^)]*?))\)|\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match;
    let idx = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      if (match[1] && match[2]) {
        const isExternal = match[2].startsWith('http');
        parts.push(
          <a
            key={`${keyPrefix}l${idx}`}
            href={match[2]}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="text-[hsl(var(--brand-gold-500))] underline underline-offset-2 hover:text-white transition-colors"
          >{match[1]}</a>
        );
      } else if (match[3]) {
        parts.push(<strong key={`${keyPrefix}b${idx}`} className="text-white">{match[3]}</strong>);
      }
      lastIndex = match.index + match[0].length;
      idx++;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      // Table block
      if (line.trimStart().startsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trimStart().startsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        const parseCells = (row) => row.split('|').slice(1, -1).map(c => c.trim());
        const headerCells = parseCells(tableLines[0]);
        const dataRows = tableLines.filter((_, idx) => idx > 1).map(parseCells);
        elements.push(
          <div key={`tbl-${i}`} className="my-6 overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[hsl(var(--accent))]">
                  {headerCells.map((c, ci) => <th key={ci} className="px-4 py-3 text-left text-white font-semibold border-b border-[hsl(var(--border))]">{parseInline(c, `th${i}${ci}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? '' : 'bg-[hsl(var(--accent))]/30'}>
                    {row.map((c, ci) => <td key={ci} className="px-4 py-3 text-gray-300 border-b border-[hsl(var(--border))]/50">{parseInline(c, `td${i}${ri}${ci}`)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
      if (line.startsWith('### ')) {
        elements.push(<h3 key={`h3-${i}`} className="text-xl font-bold text-white mt-8 mb-3">{parseInline(line.replace('### ', ''), `h3${i}`)}</h3>);
      } else if (line.startsWith('## ')) {
        elements.push(<h2 key={`h2-${i}`} className="text-2xl font-bold text-white mt-10 mb-4">{parseInline(line.replace('## ', ''), `h2${i}`)}</h2>);
      } else if (line.startsWith('# ')) {
        elements.push(<h1 key={`h1-${i}`} className="text-3xl font-bold text-[hsl(var(--brand-gold-500))] mt-12 mb-5">{parseInline(line.replace('# ', ''), `h1${i}`)}</h1>);
      } else if (line.trim() === '---') {
        elements.push(<hr key={`hr-${i}`} className="my-8 border-[hsl(var(--border))]" />);
      } else if (line.match(/^\d+\.\s/)) {
        elements.push(<p key={`ol-${i}`} className="text-gray-300 leading-relaxed mb-2 pl-4">{parseInline(line, `ol${i}`)}</p>);
      } else if (line.startsWith('- ')) {
        elements.push(<p key={`li-${i}`} className="text-gray-300 leading-relaxed mb-1 pl-4">{parseInline(line.substring(2), `li${i}`)}</p>);
      } else if (line.trim() === '') {
        elements.push(<div key={`br-${i}`} className="h-2" />);
      } else {
        elements.push(<p key={`t-${i}`} className="text-gray-300 leading-relaxed mb-2">{parseInline(line, `t${i}`)}</p>);
      }
      i++;
    }
    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[hsl(var(--brand-gold-500))]"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="blog-post-page">
      {/* Header */}
      <header className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="JepPrep" className="h-10" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> All Posts
            </Link>
            {user && (
              <Link to="/dashboard">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold text-sm" data-testid="blogpost-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Article Header */}
        <article>
          <div className="mb-8">
            <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.published_at)}</span>
              <span className="text-gray-700">|</span>
              <span>{post.author}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[hsl(var(--brand-gold-500))] mb-4 leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="text-lg text-gray-400">{post.excerpt}</p>
            <div className="flex gap-2 mt-4">
              {post.tags?.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs border-[hsl(var(--border))] text-gray-500">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[hsl(var(--border))] mb-8" />

          {/* Content */}
          <div className="prose-custom" data-testid="blog-post-content">
            {renderContent(post.content)}
          </div>
        </article>

        {/* CTA */}
        <div className="mt-16 text-center p-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to start training?</h2>
          <p className="text-gray-400 mb-6">Put these strategies to work with JepPrep's free trivia practice.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-bold rounded-lg hover:bg-[hsl(var(--brand-gold-400))] transition-colors"
            data-testid="blog-post-cta"
          >
            Get Started Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Back to Blog */}
        <div className="mt-12 text-center">
          <Link
            to="/blog"
            className="text-[hsl(var(--brand-gold-500))] hover:text-white transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all posts
          </Link>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-[hsl(var(--border))]">
          <div className="flex flex-col items-center gap-3">
            <img src={LOGO_URL} alt="JepPrep" className="h-10" />
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/blog" className="hover:text-white transition-colors text-[hsl(var(--brand-gold-500))]">Blog</Link>
            </div>
            <p className="text-gray-600 text-xs text-center max-w-xl">
              © 2026 JepPrep. Not affiliated with Jeopardy!, Sony Pictures Television, or their subsidiaries.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
